/**
 * ── THE LIBRARY CATALOG — `axon.library/1` (plan §2.2, §3; ADR-0003 decisions 1, 2, 3, 6) ───────
 *
 * The catalog is the DATA PLANE's index: asset id → one immutable, content-hashed version with its
 * files, its dependencies, its capability gate and its bindings. It never knows a user (§1), never
 * carries code (ADR-0003 §3) and never decides affordability. This module is the ONE place its
 * shape is stated (zod — every external shape is), and the three pure folds over it that the
 * service, the editor and the entitlement adapter all read:
 *
 *   parseCatalog(json)       untrusted bytes → a typed catalog, or a refusal, plus WARNINGS as data
 *   mergeCatalogs(p, ...s)   secondary catalogs (a season drop, a marketplace pack) merged by id —
 *                            a later id wins ONLY if its version is newer; ties go to the primary
 *                            (§2.1 — Unity's `LoadContentCatalogAsync` shape)
 *   resolveAsset(cat, id)    deps flattened depth-first into LOAD ORDER, cycle-capped at 8, missing
 *                            deps LISTED rather than thrown (rule 6)
 *   checkAssetRequires(...)  the capability gate, exactly `world.requires`' shape (`schema.ts`
 *                            `checkRequires`) plus the decoder formats the world gate lacks
 *
 * ⚠️ WHY THE STRICTNESS IS AT PARSE AND THE LENIENCY IS AT RESOLVE. A malformed ENTRY (a bad id,
 * a bad blob sha, an unknown kind) refuses the whole catalog: a catalog is a published, CI-
 * verified artifact, and a secondary catalog that fails wholesale leaves the primary intact. A
 * DANGLING dependency, by contrast, is a warning at parse and a `missing[]` row at resolve — a
 * secondary catalog merged later may legitimately supply it, and an asset whose dep is absent is
 * `failed('missing')` at load time, never a throw.
 *
 * ── ⭐ P1.5 · TWO HASHES, TWO JOBS, NEVER CONFUSED (plan §13.1) ──────────────────────────────
 *
 * There are exactly two content hashes in this system and conflating them is how a content-
 * addressed library rots:
 *
 *   `asset.hash`      THE IDENTITY OF THE ASSET'S WHOLE COOKED FORM — its kind, its authored
 *                     params and the SET of blob hashes its roles name, folded into one 12-hex
 *                     word. It is the LEDGER KEY with `id` (`ledgerKey(id, hash)`), i.e. the unit
 *                     of LIFETIME. It moves when a label, a binding or a dep moves, even though
 *                     not one byte on the wire changed.
 *   `files[r].sha256` THE IDENTITY OF ONE FILE'S BYTES — a full sha256 over exactly the bytes
 *                     served at `<origin>/library/blobs/<sha256>.<ext>`. It is the unit of
 *                     TRANSFER and it is SHARED: two assets whose `mesh` role names one sha256
 *                     are ONE download and ONE GPU upload, at two lifetimes.
 *
 * So a material's params can change with no blob moving (asset.hash moves, nothing re-downloads),
 * and a blob can be named by assets whose hashes differ (one transfer, N lifetimes). A `files`
 * entry therefore carries NO `path`: the URL is `blobUrl(origin, sha256, ext)` and there is no
 * per-asset directory left to put a path in.
 *
 * PURE (`PURE_DIRS`): zod is allowed (the `world/schema.ts` precedent — the schema IS the shape).
 * No three, no DOM, no clock. Warnings are returned, never printed.
 */

import { z } from 'zod';

// `.ts` on purpose: `tools/library/**` loads this module under `node --experimental-strip-types`,
// whose resolver needs the full specifier (`world/collisionFile.ts` sets the precedent).
import { decodeKeyFor } from './decode.ts';

// ── Vocabulary ─────────────────────────────────────────────────────────────────────────────────

export const LIBRARY_SCHEMA_ID = 'axon.library/1' as const;

/**
 * The closed kind enum (plan §3, ADR-0003 §6). Growing it is an ADR amendment, like the category
 * set in ADR-0002 — which is why it is a tuple here and nowhere else.
 */
export const ASSET_KINDS = [
  'mesh',
  'skinned',
  'body',
  'clip',
  'texture',
  'material',
  'audio',
  'entity',
] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/**
 * The file ROLES each kind must carry to be instantiable (plan §3's "files (roles)" column).
 * `thumb` is never required — it is a browsing nicety. A kind missing a required role parses
 * (the catalog stays usable) and WARNS; the service resolves such an asset to `failed('missing')`.
 */
export const KIND_REQUIRED_ROLES: Record<AssetKind, readonly string[]> = {
  mesh: ['mesh'],
  skinned: ['mesh'],
  body: ['mesh', 'rig'],
  clip: ['clip'],
  texture: ['image'],
  /*
   * ⭐ P1.5·A2 — A MATERIAL CARRIES NO FILE, AND THAT IS PLAN §13.1's OWN SENTENCE MADE
   * STRUCTURAL. "A material's params can change with no blob moving" is only true if the params
   * are not IN a blob: a `params.json` role would put every archetype tweak on the wire as a new
   * sha256 and make `extends` a fold over files nobody has fetched yet. So a material's whole
   * definition is the catalog's `material` block (below), an empty `files` map is legal for this
   * kind alone, and a params edit moves `asset.hash` and downloads nothing. P1's `['params']`
   * row was written before the material shape was decided (plan §3's table) and no material
   * asset was ever published against it.
   */
  material: [],
  audio: ['audio'],
  /*
   * ⭐ P1.5·C — AN ENTITY CARRIES NO FILE EITHER, AND THE REASON IS STRONGER THAN THE MATERIAL'S.
   * Plan §3's table gave it a `params` (json) role, written before §13.6 said what an entity IS:
   * a library-level PREFAB, `{ class, params, deps }` — ADR-0002 tier (b) lifted out of one world.
   * A world's prefab table is DATA IN THE MANIFEST, not a file beside it, and the same must hold
   * here for a reason a material's does not have: `requires.classes` is a PRE-FETCH gate — the
   * editor greys an unsupported asset, the store hides it, and `acquire` refuses it before a byte
   * moves (§2.2). A `class` that lives inside a blob cannot be gated before that blob is fetched,
   * so the gate would have to run after the download it exists to prevent. The definition is the
   * catalog's `entity` block below; no entity asset was ever published against the old row.
   */
  entity: [],
};

/** Global, kebab-case ids — the SAME rule `world/schema.ts` applies to world-private asset ids. */
export const ASSET_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
/** A content hash as it appears in a path: lowercase hex, `sha256-12` (12) up to a full digest. */
export const CONTENT_HASH_PATTERN = /^[0-9a-f]{12,64}$/;
/** A file role: `mesh`, `rig`, `thumb`, `image`, … — a short lowercase word. */
export const FILE_ROLE_PATTERN = /^[a-z][a-z0-9]*$/;
/** `glb`, `glb+meshopt`, `png`, `ktx2`, `json`, `ogg` — a format plus optional `+codec` suffixes. */
export const FILE_FORMAT_PATTERN = /^[a-z0-9]+(\+[a-z0-9]+)*$/;
/** A blob's file extension — the ONE thing after the dot in `/library/blobs/<sha256>.<ext>`. */
export const BLOB_EXT_PATTERN = /^[a-z0-9]{1,8}$/;
/** A blob's identity: a FULL sha256 over its bytes. Never truncated — this IS the CDN path. */
export const BLOB_SHA256_PATTERN = /^[0-9a-f]{64}$/;
/** Licences are kebab words; this one is the value `publish:library` refuses (plan §7). */
export const LOCAL_ONLY_LICENCE = 'local-only';

/** Depth at which dependency flattening stops and warns (plan §4.1 "cycle cap 8"). */
export const MAX_DEP_DEPTH = 8;

// ── Materials: inheritance, and why it is one kind's privilege (plan §13.3) ─────────────────────

/**
 * ⭐ `extends: <assetId>` IS LEGAL ON KIND `material` AND ON NOTHING ELSE, and the restriction is
 * a decision rather than a v1 shortcut. "Inherit" means something exact for a PARAMETERISED
 * material — a child that overrides two of its parent's four texture slots and keeps the other
 * two is the case the whole two-level cache exists to get right (plan §13.7) — and it means
 * nothing at all for a mesh or a clip, whose content is BYTES: a mesh that "extends" another is
 * either the same bytes, which content addressing already shares for free, or different bytes,
 * which is a different asset. Growing this to a second kind is an ADR-0003 amendment, like the
 * kind enum itself.
 */
export const MATERIAL_INHERITING_KIND = 'material' as const satisfies AssetKind;

/**
 * The factory's archetype vocabulary. ⚠️ A MIRROR of `ARCHETYPES` in
 * `src/render/materials/surfaceFactory.ts`, NOT an import: that module value-imports `three`, and
 * this directory is PURE (`PURE_DIRS`). `tests/assets/core/material.test.ts` reads the factory's
 * source and pins the two lists together, so the copy cannot drift silently — the same treatment
 * `placement.scaleTriple` gets against `world/collision.scaleVec3`.
 */
export const MATERIAL_ARCHETYPES = [
  'surf.opaque',
  'surf.alphaTest',
  'surf.emissive',
  'surf.unlit',
  'surf.maskedRGB',
] as const;
export type MaterialArchetype = (typeof MATERIAL_ARCHETYPES)[number];

/** A factory texture slot — `map`, `normalMap`, `orm`, `emissiveMap`, `mask`. camelCase. */
export const MATERIAL_SLOT_PATTERN = /^[a-z][A-Za-z0-9]*$/;

/**
 * Depth at which the `extends` chain stops and warns — the SAME 8 `world/prefabs.ts` caps its
 * chains at and `MAX_DEP_DEPTH` caps the dependency walk at. It counts ANCESTORS, so a material
 * plus eight parents folds and a ninth truncates.
 */
export const MATERIAL_EXTENDS_CAP = 8;

/**
 * The BOTTOM of the nearest-wins fold: what `makeSurface(archetype, {})` does when no level of
 * the chain says. Read off `surfaceFactory.makeSurface`, per key, and pinned to its source.
 *
 * ⚠️ TWO DEFAULTS ARE DELIBERATELY ABSENT, because a flat table would have to lie about them:
 *  · `metalness` — the factory's default is `tex.orm ? 1 : 0`, i.e. a function of what is BOUND,
 *    and a table entry would be wrong in whichever half of the cases it did not pick.
 *  · the three `maskedRGB` scalars (brightness 2, contrast 1, saturation 1) — they live inside a
 *    NESTED block, and the fold below is SHALLOW (`prefabs.ts`'s rule, one layer over): the
 *    nearest level that declares `maskedRGB` replaces the whole block. A default inside it would
 *    vanish the moment a child touched one field, which is worse than not being there.
 * `makeSurface` supplies both per field at build time, where the bound textures are known.
 */
export const MATERIAL_ARCHETYPE_DEFAULTS: Readonly<Record<MaterialArchetype, Readonly<Record<string, unknown>>>> = {
  'surf.opaque': { roughness: 1 },
  'surf.alphaTest': { roughness: 1, alphaTest: 0.5 },
  'surf.emissive': { roughness: 1, emissive: 0xffffff, emissiveIntensity: 1 },
  'surf.unlit': {},
  'surf.maskedRGB': { roughness: 1 },
};

// ── Schema ─────────────────────────────────────────────────────────────────────────────────────

const AssetId = z.string().regex(ASSET_ID_PATTERN, 'asset id must be kebab-case (a-z, 0-9, dashes)');
const ContentHash = z.string().regex(CONTENT_HASH_PATTERN, 'hash must be 12-64 lowercase hex chars');

/**
 * ONE BLOB, named by a role. There is no `path` and no per-asset directory: `sha256` + `ext` ARE
 * the address (`uri.blobUrl`), which is why both are pattern-fenced here — they become URL
 * segments verbatim, and this is the one place untrusted bytes become one. A `..`, a slash, a
 * query or a scheme survives neither pattern, so the escape check the old relative path needed
 * is now structural rather than a refinement.
 */
export const AssetFileSchema = z.object({
  sha256: z.string().regex(BLOB_SHA256_PATTERN, 'sha256 must be 64 lowercase hex chars'),
  bytes: z.number().int().nonnegative(),
  format: z.string().regex(FILE_FORMAT_PATTERN, 'format must look like `glb` or `glb+meshopt`'),
  /** The blob's file extension — `glb`, `png`, `json`. Lowercase alphanumeric, no dot. */
  ext: z.string().regex(BLOB_EXT_PATTERN, 'ext must be a short lowercase word (`glb`, `png`, `json`)'),
});
export type AssetFile = z.infer<typeof AssetFileSchema>;

export const AssetRequiresSchema = z.object({
  classes: z.array(z.string()).default([]),
  formats: z.array(z.string()).default([]),
});
export type AssetRequires = z.infer<typeof AssetRequiresSchema>;

export const AssetBindingsSchema = z.object({
  /** The backend's cosmetic UUID (`user_cosmetics`), when known. Wins over the tag when it lands. */
  cosmeticId: z.string().nullable().default(null),
  /** UE's `ue_object_tag` (`SK_Body_Blue_001`) — the bridge until the backend ships `web_asset_id`. */
  ueObjectTag: z.string().nullable().default(null),
});
export type AssetBindings = z.infer<typeof AssetBindingsSchema>;

export const AssetSocketSchema = z.object({
  bone: z.string().min(1),
  offset: z.tuple([z.number(), z.number(), z.number()]),
});
export type AssetSocket = z.infer<typeof AssetSocketSchema>;

export const AssetAvailabilitySchema = z.object({
  from: z.iso.datetime().nullable().default(null),
  until: z.iso.datetime().nullable().default(null),
});
export type AssetAvailability = z.infer<typeof AssetAvailabilitySchema>;

/**
 * ⭐ A MATERIAL ASSET'S WHOLE DEFINITION, IN THE CATALOG (plan §13.4's shape, §13.1's rule).
 *
 * `{ archetype, params, textures: { slot: assetId } }` beside the asset's own `extends`. It is
 * catalog DATA and not a blob for the reason §13.1 states in one sentence — "a material's params
 * can change with no blob moving" — which is only true if the params are not in a file. It also
 * makes `resolveMaterial` a PURE fold over an already-loaded index: resolving a chain fetches
 * nothing, so the editor, the store and the budget projection can all ask what a material is
 * before a byte moves.
 *
 * `textures` maps a FACTORY SLOT to a library asset id — never to a URL and never to a blob. The
 * ids become the asset's DERIVED `deps` (plan §13.3), so the machinery that already flattens a
 * dependency closure is what makes the textures resident; nothing here loads anything.
 *
 * `archetype` is nullable so a CHILD may inherit its parent's; a chain in which no level names one
 * is a warning at resolve, never a throw (rule 6) — the factory has nothing to build and the
 * caller's placeholder policy is what shows.
 */
export const MaterialDefSchema = z.object({
  archetype: z.enum(MATERIAL_ARCHETYPES).nullable().default(null),
  /** `SurfaceParams`, as data. Opaque here on purpose: the factory owns their meaning, not the catalog. */
  params: z.record(z.string(), z.unknown()).default({}),
  /** factory slot → texture ASSET ID (`map`, `normalMap`, `orm`, `emissiveMap`, `mask`). */
  textures: z.record(z.string().regex(MATERIAL_SLOT_PATTERN, 'texture slot must be a factory slot name'), AssetId).default({}),
});
export type MaterialDef = z.infer<typeof MaterialDefSchema>;

/** One level of an `extends` chain, as the fold reads it — a `MaterialDef` plus its parent link. */
export interface MaterialNode {
  archetype: MaterialArchetype | null;
  params: Readonly<Record<string, unknown>>;
  textures: Readonly<Record<string, string>>;
  extends: string | null;
}

// ── Entities: a library-level prefab, and code never travels with it (plan §13.6) ──────────────

/**
 * ⭐ THE `entity` KIND IS THE CODE-BEARING ONE, AND IT CARRIES NO CODE.
 *
 * `{ class, params }` beside the asset's DERIVED `deps`: a library-level prefab, ADR-0002 tier (b)
 * lifted out of a single world. `class` is a REGISTRY NAME and nothing else — the runtime deploys
 * whole and downloads partial (ADR-0001), so the behaviour is a lazy chunk the build already
 * shipped and the asset only ever names it. That is what keeps ADR-0003 decision 3 ("assets never
 * carry code") true while still answering "a solar system simulation with code" (plan §3).
 *
 * The class is gated exactly as a world's placements are: `composeEntry` folds it into
 * `requires.classes`, so `checkAssetRequires` refuses an asset this runtime lacks the class for
 * BEFORE a byte moves — `failed('unsupported')`, warned once, never a throw (rule 6, §2.2). That
 * pre-fetch gate is the reason the definition is catalog data rather than a `params.json` blob:
 * a class inside a file could only be gated after the download the gate exists to prevent.
 *
 * `params` is opaque here on purpose, exactly as a material's is: the REGISTRY CLASS owns their
 * meaning (its zod schema validates them at spawn), not the catalog. Placement fields still win
 * over them — the prefab precedence of ADR-0002, unchanged and un-duplicated, because a `lib:`
 * placement resolves through `world/prefabs.resolveEntityDef` with this block as its table entry.
 */
export const ENTITY_PREFAB_KIND = 'entity' as const satisfies AssetKind;

export const ENTITY_CLASS_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

export const EntityDefSchema = z.object({
  /** A registry class name (`solar_system_sim`), snake_case — never a module path, never code. */
  class: z.string().regex(ENTITY_CLASS_PATTERN, 'class must be a snake_case registry class name'),
  /** The prefab's params, as data. The class's own schema owns their meaning, not the catalog. */
  params: z.record(z.string(), z.unknown()).default({}),
});
export type EntityDef = z.infer<typeof EntityDefSchema>;

export const LibraryAssetSchema = z.object({
  kind: z.enum(ASSET_KINDS),
  version: z.number().int().positive(),
  hash: ContentHash,
  label: z.string().min(1),
  description: z.string().default(''),
  /** role → BLOB. Role keys are unique BY CONSTRUCTION (a JSON object); two roles may share a blob. */
  files: z.record(z.string().regex(FILE_ROLE_PATTERN, 'file role must be a lowercase word'), AssetFileSchema),
  /**
   * Asset ids this one needs resident to be usable. Dangling ids WARN at parse, LIST at resolve.
   *
   * ⭐ SINCE P1.5·A2 THIS IS DERIVED, NEVER AUTHORED (plan §13.3). The cook computes it from what
   * an asset actually references — a material's resolved texture slots plus its `extends` parent,
   * an entity's declared data assets, a mesh's external textures — and `tools/library/authoring.ts`
   * REFUSES a `deps` key in an `asset.json` with that sentence. The catalog still carries the
   * field, because the catalog is what the runtime reads; what changed is who writes it.
   */
  deps: z.array(AssetId).default([]),
  requires: AssetRequiresSchema.prefault({}),
  labels: z.array(z.string()).default([]),
  bindings: AssetBindingsSchema.prefault({}),
  /** For `skinned`/`mesh` worn on the character: the slot vocabulary is data (§5.2). */
  slot: z.string().nullable().default(null),
  /** For attachments: a bone name + offset, authored in the catalog (UE sockets do not export). */
  socket: AssetSocketSchema.nullable().default(null),
  /** What the accountant charges at `fetching`, before the bytes arrive (§4.4). */
  vramEstimateMB: z.number().nonnegative().default(0),
  /** LiveOps hook, unused in v1. */
  availability: AssetAvailabilitySchema.nullable().default(null),
  /**
   * ⭐ kind `material` ONLY (plan §13.3) — the parent this one overrides. See
   * `MATERIAL_INHERITING_KIND` for why exactly one kind has it. Refused with a sentence on every
   * other kind by the catalog-wide refine below; a DANGLING parent is a warning, not a refusal,
   * because a secondary catalog merged later may legitimately supply it.
   */
  extends: AssetId.nullable().default(null),
  /** ⭐ kind `material` ONLY — the definition itself. A material's data is catalog, not a blob. */
  material: MaterialDefSchema.nullable().default(null),
  /**
   * ⭐ kind `entity` ONLY (plan §13.6) — the prefab: a registry class NAME plus its params. See
   * {@link EntityDefSchema}: code never travels with an asset, and the class is gated through
   * `requires.classes` before a byte moves.
   */
  entity: EntityDefSchema.nullable().default(null),
  licence: z.string().regex(ASSET_ID_PATTERN, 'licence must be a kebab word (`axon`, `cc-by`, `local-only`)'),
});
/*
 * ⚠️ THE OLD "file paths must be unique within an asset" REFINE IS GONE, AND ITS ABSENCE IS THE
 * POINT. Two roles naming ONE sha256 is now LEGAL and is the scheme working — the same bytes
 * serve both roles, once. What must still hold is the CATALOG-WIDE rule below: a sha256 may not
 * disagree with itself about its `ext` or its `bytes`, or one blob would have two URLs.
 */
export type LibraryAsset = z.infer<typeof LibraryAssetSchema>;

/**
 * `slots` (§5.2) is P2 data: either a `$ref` to the sidecar file or an inline table. Accepted and
 * passed through so a v1 catalog that already names it does not refuse; nothing in v1 reads it.
 */
export const CatalogSlotsSchema = z.union([
  z.object({ $ref: z.string().min(1) }),
  z.record(z.string(), z.unknown()),
]);

export const LibraryCatalogSchema = z
  .object({
    schema: z.literal(LIBRARY_SCHEMA_ID),
    generatedAt: z.iso.datetime(),
    /** Keyed by GLOBAL kebab id — uniqueness is structural, the key rule is the refine. */
    assets: z.record(AssetId, LibraryAssetSchema),
    slots: CatalogSlotsSchema.optional(),
  })
  .superRefine((cat, ctx) => {
    // A record key IS unique, but nothing above stops two entries sharing a HASH under different
    // ids at the same version — which would be two names for one directory on the CDN. Not an
    // error (a re-published identical asset under a new id is legal) — but an (id, hash) pair
    // must be unique, because the ledger is keyed by it.
    const seen = new Set<string>();
    /*
     * A blob is addressed by `<sha256>.<ext>`, so one sha256 must carry ONE ext and ONE size
     * across the whole catalog — otherwise two assets build two different URLs for one blob, or
     * agree on the URL and disagree about what arrives.
     *
     * ⭐ AND ONE DECODE, WHICH IS THE SAME KIND OF FACT AND WAS NOT CHECKED. `format` feeds the
     * texture decode key and was unchecked; the KEY itself (`decodeKeyFor` — kind, role, `format`
     * and the `map-*` / `colourspace-*` labels, all of it catalog data) was checked only at
     * `acquireBlob`, i.e. after the bytes had shipped. Two assets over one sha256 that read it
     * differently — one greyscale map authored as a `mask` and as a `data` map, which is ordinary
     * authoring — cannot both be served: whichever attaches SECOND fails `unsupported`, its
     * material slot degrades to the archetype default, and WHICH one loses flips with load order
     * and with disposal. Refusing here names BOTH ids before a byte moves; the runtime guard stays
     * as belt-and-braces for a secondary catalog merged at runtime, which no publish ever saw.
     */
    const blobs = new Map<string, { ext: string; bytes: number; format: string; decode: string; where: string }>();
    for (const [id, asset] of Object.entries(cat.assets)) {
      const key = `${id}@${asset.hash}`;
      if (seen.has(key)) {
        ctx.addIssue({ code: 'custom', message: `duplicate (id, hash) pair ${key}`, path: ['assets', id] });
      }
      seen.add(key);
      /*
       * ⭐ THE KIND RESTRICTION, ENFORCED WITH A SENTENCE (plan §13.3). It is a REFUSAL and not a
       * warning because, unlike a dangling id, no later merge can make it true: `extends` on a
       * mesh is a statement the system has no meaning for, and a catalog that ships one has an
       * author who believes something is happening.
       */
      if (asset.kind !== MATERIAL_INHERITING_KIND) {
        if (asset.extends !== null) {
          ctx.addIssue({
            code: 'custom',
            message: `'${id}' is a ${asset.kind} and carries 'extends' — inheritance means something for a parameterised ${MATERIAL_INHERITING_KIND} and nothing for a kind whose content is bytes; two meshes with the same bytes are already one blob`,
            path: ['assets', id, 'extends'],
          });
        }
        if (asset.material !== null) {
          ctx.addIssue({
            code: 'custom',
            message: `'${id}' is a ${asset.kind} and carries a 'material' block, which only a ${MATERIAL_INHERITING_KIND} has`,
            path: ['assets', id, 'material'],
          });
        }
      } else if (asset.material === null) {
        ctx.addIssue({
          code: 'custom',
          message: `'${id}' is a ${MATERIAL_INHERITING_KIND} with no 'material' block — a material's definition is catalog data, not a file, so there is nothing else it could be`,
          path: ['assets', id, 'material'],
        });
      }
      /*
       * ⭐ THE SAME RULE FOR THE `entity` BLOCK, and a REFUSAL for the same reason: an `entity`
       * block on a mesh is a statement the system has no meaning for, and no later merge can make
       * it true. An entity WITHOUT one has nothing else it could be — it carries no file.
       */
      if (asset.kind !== ENTITY_PREFAB_KIND) {
        if (asset.entity !== null) {
          ctx.addIssue({
            code: 'custom',
            message: `'${id}' is a ${asset.kind} and carries an 'entity' block, which only an ${ENTITY_PREFAB_KIND} has — a prefab names a registry class, and a kind whose content is bytes names none`,
            path: ['assets', id, 'entity'],
          });
        }
      } else if (asset.entity === null) {
        ctx.addIssue({
          code: 'custom',
          message: `'${id}' is an ${ENTITY_PREFAB_KIND} with no 'entity' block — a prefab's definition (class + params) is catalog data, not a file, so there is nothing else it could be`,
          path: ['assets', id, 'entity'],
        });
      }
      for (const [role, f] of Object.entries(asset.files)) {
        const decode = decodeKeyFor(asset, role, f);
        const where = `${id}.${role}`;
        const held = blobs.get(f.sha256);
        if (held === undefined) {
          blobs.set(f.sha256, { ext: f.ext, bytes: f.bytes, format: f.format, decode, where });
          continue;
        }
        if (held.ext !== f.ext || held.bytes !== f.bytes || held.format !== f.format) {
          ctx.addIssue({
            code: 'custom',
            message: `blob ${f.sha256.slice(0, 12)}… is ${f.ext}/${f.format}/${f.bytes} B here and ${held.ext}/${held.format}/${held.bytes} B at ${held.where}`,
            path: ['assets', id, 'files', role],
          });
        }
        if (held.decode !== decode) {
          ctx.addIssue({
            code: 'custom',
            message: `blob ${f.sha256.slice(0, 12)}… is decoded as '${decode}' here and as '${held.decode}' at ${held.where} — one blob is ONE download and ONE decode, so identical bytes may not be read two ways; give the second asset its own file, or make the two agree`,
            path: ['assets', id, 'files', role],
          });
        }
      }
    }
  });
export type LibraryCatalog = z.infer<typeof LibraryCatalogSchema>;

/** `/library/catalog/latest.json` — the tiny mutable pointer at the immutable catalog (§2.1). */
export const CatalogPointerSchema = z.object({ hash: ContentHash });
export type CatalogPointer = z.infer<typeof CatalogPointerSchema>;

// ── parse ──────────────────────────────────────────────────────────────────────────────────────

export type ParseCatalogResult =
  | { ok: true; catalog: LibraryCatalog; warnings: string[] }
  | { ok: false; error: string };

/** An empty, valid catalog — what the service holds before any pointer has been read. */
export function emptyCatalog(generatedAt = '1970-01-01T00:00:00.000Z'): LibraryCatalog {
  return { schema: LIBRARY_SCHEMA_ID, generatedAt, assets: {} };
}

/**
 * Untrusted JSON → a catalog. Structural faults REFUSE (`ok: false`, one sentence naming the path);
 * authoring faults that leave the catalog usable WARN, as strings the service prints once:
 *  · a dep naming an id the catalog does not carry (a secondary catalog may supply it);
 *  · an asset depending on itself (ignored at resolve);
 *  · a kind missing one of its required roles (`failed('missing')` at load).
 */
export function parseCatalog(json: unknown): ParseCatalogResult {
  const parsed = LibraryCatalogSchema.safeParse(json);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const where = first && first.path.length > 0 ? ` at ${first.path.map(String).join('.')}` : '';
    return { ok: false, error: `catalog refused${where}: ${first?.message ?? 'invalid'}` };
  }
  const catalog = parsed.data;
  const warnings: string[] = [];
  for (const [id, asset] of Object.entries(catalog.assets)) {
    for (const dep of asset.deps) {
      if (dep === id) warnings.push(`asset '${id}' depends on itself (ignored)`);
      else if (!(dep in catalog.assets)) warnings.push(`asset '${id}' depends on '${dep}', which this catalog does not carry`);
    }
    for (const role of KIND_REQUIRED_ROLES[asset.kind]) {
      if (!(role in asset.files)) warnings.push(`asset '${id}' (kind ${asset.kind}) has no '${role}' file`);
    }
    // The `extends` link gets the dependency treatment, for the dependency reason: a secondary
    // catalog merged later may supply the parent, so a dangling one is a warning here and a
    // `missing[]` row at resolve, never a refusal.
    /*
     * ⭐ THE CLASS MUST BE IN THE GATE, OR THE GATE CANNOT REFUSE IT. `composeEntry` folds an
     * entity's `class` into `requires.classes`, so this can only fire on a hand-edited or
     * hand-built catalog — and if it does, the asset would resolve `ready` in a runtime that has
     * no such class and fail at SPAWN instead of at ACQUIRE, which is a warn-and-skip in the host
     * where §13.6 promised a `failed('unsupported')` before a byte moved.
     */
    if (asset.entity !== null && !asset.requires.classes.includes(asset.entity.class)) {
      warnings.push(
        `asset '${id}' is an entity naming class '${asset.entity.class}', which its requires.classes does not list — ` +
          `the capability gate cannot refuse it in a runtime that lacks the class`,
      );
    }
    if (asset.extends !== null) {
      const parent = catalog.assets[asset.extends];
      if (asset.extends === id) warnings.push(`asset '${id}' extends itself (ignored)`);
      else if (parent === undefined) warnings.push(`asset '${id}' extends '${asset.extends}', which this catalog does not carry`);
      else if (parent.kind !== MATERIAL_INHERITING_KIND) warnings.push(`asset '${id}' extends '${asset.extends}', which is a ${parent.kind} and not a ${MATERIAL_INHERITING_KIND}`);
    }
  }
  return { ok: true, catalog, warnings };
}

export type ParsePointerResult = { ok: true; hash: string } | { ok: false; error: string };

export function parseCatalogPointer(json: unknown): ParsePointerResult {
  const parsed = CatalogPointerSchema.safeParse(json);
  if (!parsed.success) return { ok: false, error: `catalog pointer refused: ${parsed.error.issues[0]?.message ?? 'invalid'}` };
  return { ok: true, hash: parsed.data.hash };
}

// ── merge ──────────────────────────────────────────────────────────────────────────────────────

/**
 * Merge secondary catalogs INTO the primary, by id. For a shared id the HIGHER `version` wins;
 * on a tie the earlier argument wins (so the primary beats every secondary, and the first
 * secondary beats the second). `schema`, `generatedAt` and `slots` come from the primary — a
 * season drop does not get to redefine the slot vocabulary. Pure: returns a new object, inputs
 * untouched.
 */
export function mergeCatalogs(primary: LibraryCatalog, ...secondary: readonly LibraryCatalog[]): LibraryCatalog {
  const assets: Record<string, LibraryAsset> = { ...primary.assets };
  for (const cat of secondary) {
    for (const [id, asset] of Object.entries(cat.assets)) {
      const held = assets[id];
      if (held === undefined || asset.version > held.version) assets[id] = asset;
    }
  }
  const out: LibraryCatalog = { schema: primary.schema, generatedAt: primary.generatedAt, assets };
  if (primary.slots !== undefined) out.slots = primary.slots;
  return out;
}

// ── resolve ────────────────────────────────────────────────────────────────────────────────────

export interface ResolvedAsset {
  id: string;
  /** `null` when the id is not in the catalog — then `found` is false and `missing` lists the id. */
  asset: LibraryAsset | null;
  found: boolean;
  /**
   * Every transitive dependency PRESENT in the catalog, deduplicated, in LOAD ORDER: a dependency
   * always precedes anything that needs it (depth-first post-order). The root is not included.
   */
  deps: readonly string[];
  /** Ids referenced (by the root or a dep) that the catalog does not carry, in first-seen order. */
  missing: readonly string[];
  /** Cycle and depth-cap sentences — data for the service to warn once. */
  warnings: readonly string[];
  /** `true` when the depth cap cut the walk short: `deps` is then a PREFIX of the truth. */
  truncated: boolean;
  /** The capability gate, unioned over the root and every listed dep (sorted, deduplicated). */
  requires: { classes: readonly string[]; formats: readonly string[] };
  /** What the accountant charges up front for the whole set, root included. */
  vramEstimateMB: number;
}

/**
 * Flatten an asset's dependency closure. Never throws: a missing id, a cycle and a runaway chain
 * are all STATES on the result (rule 6). Deterministic — the walk follows `deps[]` in authored
 * order, so the same catalog always yields the same list.
 */
export function resolveAsset(catalog: LibraryCatalog, id: string): ResolvedAsset {
  const root = catalog.assets[id];
  if (root === undefined) {
    return {
      id,
      asset: null,
      found: false,
      deps: [],
      missing: [id],
      warnings: [],
      truncated: false,
      requires: { classes: [], formats: [] },
      vramEstimateMB: 0,
    };
  }
  const order: string[] = [];
  const done = new Set<string>([id]);
  const missing: string[] = [];
  const missingSeen = new Set<string>();
  const warnings: string[] = [];
  let truncated = false;
  // `path` is the current DFS stack — an id already on it is a cycle, not merely a repeat.
  const path: string[] = [id];

  const visit = (depId: string, depth: number): void => {
    if (path.includes(depId)) {
      warnings.push(`dependency cycle: ${[...path, depId].join(' -> ')} (ignored)`);
      return;
    }
    if (done.has(depId)) return;
    const asset = catalog.assets[depId];
    if (asset === undefined) {
      if (!missingSeen.has(depId)) {
        missingSeen.add(depId);
        missing.push(depId);
      }
      return;
    }
    if (depth > MAX_DEP_DEPTH) {
      if (!truncated) warnings.push(`dependency chain from '${id}' exceeds depth ${MAX_DEP_DEPTH} at '${depId}' — truncated`);
      truncated = true;
      return;
    }
    path.push(depId);
    for (const next of asset.deps) visit(next, depth + 1);
    path.pop();
    done.add(depId);
    order.push(depId);
  };
  for (const dep of root.deps) visit(dep, 1);

  const classes = new Set<string>(root.requires.classes);
  const formats = new Set<string>(root.requires.formats);
  let vram = root.vramEstimateMB;
  for (const depId of order) {
    const a = catalog.assets[depId];
    if (a === undefined) continue;
    for (const c of a.requires.classes) classes.add(c);
    for (const f of a.requires.formats) formats.add(f);
    vram += a.vramEstimateMB;
  }
  return {
    id,
    asset: root,
    found: true,
    deps: order,
    missing,
    warnings,
    truncated,
    requires: { classes: [...classes].sort(), formats: [...formats].sort() },
    vramEstimateMB: vram,
  };
}

// ── resolve a material chain ───────────────────────────────────────────────────────────────────

/**
 * ── ⭐ P1.5·A2 · INHERITANCE — A NEAREST-WINS FOLD, AND IT MIRRORS `src/world/prefabs.ts` ────────
 *
 * A material asset may `extends` another; the child's own fields win, then each ancestor's in
 * turn, then the archetype's defaults. That is EXACTLY `resolveEntityDef`'s precedence one layer
 * over (ADR-0002: *entity fields > prefab params > base-class schema defaults*), and the shape is
 * deliberate — the editor, the world data and now the library all answer "what does this thing
 * actually resolve to" the same way, so an author who has learned one has learned both.
 *
 * ⚠️ IT MAY NOT IMPORT `src/world/prefabs.ts`, AND THE COPY IS FENCED RATHER THAN FORBIDDEN.
 * That module is the WORLD layer's; this directory is the library's PURE core, and a library that
 * imports a world module is the namespace collapse ADR-0003 §2 exists to prevent. So the fold is
 * written here — and `tests/assets/core/material.test.ts` drives BOTH implementations over the
 * same chains and asserts they agree, value for value, which is the only thing that stops two
 * copies of one rule drifting (the `placement.scaleTriple` precedent).
 *
 * THREE THINGS THE TWO DELIBERATELY DO NOT SHARE, each stated so a reader does not call it drift:
 *  1. `resolveEntityDef` returns `null` on a cycle — a placement it cannot resolve is a placement
 *     it must not draw. This returns a PARTIAL with `truncated: true` and a warning, because a
 *     material fold is a data read on the boot path and rule 6 forbids the throw-shaped answer;
 *     the caller degrades to the archetype's defaults, which is the placeholder policy.
 *  2. The cap counts ANCESTORS here and PREFAB LEVELS there; both are 8 and both are pinned.
 *  3. `textures` folds per SLOT beside `params` per KEY — a child overriding two of four slots
 *     keeps the other two. That is plan §13.7's whole question and there is no prefab analogue.
 *
 * The merge is SHALLOW, exactly as `{ ...params, ...acc }` is: a nearer level that declares an
 * object-valued param (`maskedRGB`) replaces the whole block rather than merging into it.
 */
export interface ResolvedMaterial {
  id: string;
  /** `false` when `id` names no material in the index — everything else is then empty. */
  found: boolean;
  /** The nearest archetype named anywhere in the chain; `null` when no level names one. */
  archetype: MaterialArchetype | null;
  /** Archetype defaults, then each ancestor, then the asset itself. Shallow, nearest wins. */
  params: Record<string, unknown>;
  /** Factory slot → texture asset id, folded per SLOT. What becomes the DERIVED texture deps. */
  textures: Record<string, string>;
  /** The chain actually folded, NEAREST FIRST: `[id, parent, grandparent, …]`. */
  chain: readonly string[];
  /** `extends` targets the index does not carry as materials, in first-seen order. */
  missing: readonly string[];
  /** Cycle and depth-cap sentences — data for the caller to warn once (rule 6). */
  warnings: readonly string[];
  /** `true` when a cycle or the cap cut the walk short: the fold is then a PARTIAL. */
  truncated: boolean;
}

/**
 * The fold over any index that can answer "what is material `<id>`". Two callers, and that is the
 * reason it takes a lookup rather than a catalog: the RUNTIME folds over a `LibraryCatalog`
 * (`resolveMaterial`), and the COOK folds over the authoring manifests before any catalog exists
 * (`tools/library/authoring.deriveDeps`). One rule, two indexes — never two folds.
 */
export function foldMaterialChain(lookup: (id: string) => MaterialNode | null, id: string): ResolvedMaterial {
  const chain: string[] = [];
  const nodes: MaterialNode[] = [];
  const missing: string[] = [];
  const warnings: string[] = [];
  const seen = new Set<string>();
  let truncated = false;
  let cursor: string | null = id;

  while (cursor !== null) {
    if (seen.has(cursor)) {
      warnings.push(`material inheritance cycle: ${[...chain, cursor].join(' -> ')} (truncated)`);
      truncated = true;
      break;
    }
    // The cap counts ANCESTORS, so `id` plus MATERIAL_EXTENDS_CAP parents folds and the next one
    // does not. A chain this deep is a cycle through distinct ids or an authoring accident; both
    // are answered by stopping and saying so.
    if (chain.length > MATERIAL_EXTENDS_CAP) {
      warnings.push(`material '${id}' extends deeper than ${MATERIAL_EXTENDS_CAP} ancestors at '${cursor}' — truncated`);
      truncated = true;
      break;
    }
    const node = lookup(cursor);
    if (node === null) {
      missing.push(cursor);
      if (cursor !== id) warnings.push(`material '${id}' extends '${cursor}', which is not a material here (ignored)`);
      break;
    }
    seen.add(cursor);
    chain.push(cursor);
    nodes.push(node);
    cursor = node.extends === cursor ? null : node.extends; // self-extends: warned at parse, ignored here
  }

  if (chain.length === 0) {
    return { id, found: false, archetype: null, params: {}, textures: {}, chain: [], missing, warnings, truncated };
  }

  let archetype: MaterialArchetype | null = null;
  for (const node of nodes) {
    if (node.archetype !== null) {
      archetype = node.archetype;
      break; // nearest wins
    }
  }
  const params: Record<string, unknown> = { ...(archetype === null ? {} : MATERIAL_ARCHETYPE_DEFAULTS[archetype]) };
  const textures: Record<string, string> = {};
  // Farthest ancestor first, so every nearer level overwrites what is beneath it.
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    Object.assign(params, nodes[i]!.params);
    Object.assign(textures, nodes[i]!.textures);
  }
  if (archetype === null) {
    warnings.push(`material '${id}' names no archetype anywhere in its chain — the factory has nothing to build`);
  }
  return { id, found: true, archetype, params, textures, chain, missing, warnings, truncated };
}

/**
 * `resolveMaterial(catalog, id)` — the fold over a parsed catalog. An id that is absent, is not a
 * `material`, or carries no `material` block resolves to `found: false` with `missing: [id]`:
 * a state, never a throw (rule 6).
 */
export function resolveMaterial(catalog: LibraryCatalog, id: string): ResolvedMaterial {
  return foldMaterialChain((key) => {
    const asset = catalog.assets[key];
    if (asset === undefined || asset.kind !== MATERIAL_INHERITING_KIND || asset.material === null) return null;
    return {
      archetype: asset.material.archetype,
      params: asset.material.params,
      textures: asset.material.textures,
      extends: asset.extends,
    };
  }, id);
}

// ── resolve an entity prefab (plan §13.6) ──────────────────────────────────────────────────────

/** What the catalog says a `lib:` entity IS — read once, by the ONE function below. */
export interface ResolvedEntityPrefab {
  id: string;
  /** False when the id is not in the catalog, is not kind `entity`, or carries no `entity` block. */
  found: boolean;
  /** The registry class name. `''` when `found` is false — never invented. */
  class: string;
  /** The prefab's params. A placement's own fields still win over these (ADR-0002 precedence). */
  params: Readonly<Record<string, unknown>>;
  /** The data assets this prefab declared, as the entry's DERIVED deps carry them (plan §13.3). */
  data: readonly string[];
  /** One sentence per reason it is not usable — data for the caller to warn once (rule 6). */
  warnings: readonly string[];
}

/**
 * ⭐ THE ONE CATALOG → PREFAB READ (plan §13.6), and the reason it is a function rather than a
 * property access at three call sites.
 *
 * Three callers want the same answer and must never disagree about it: the SERVICE composes an
 * `EntitySource` from it, the HOST resolves a `lib:<id>` PLACEMENT through it (as a prefab table
 * entry, so ADR-0002's precedence stays the one merge rule in the codebase), and the app layer's
 * runtime SUMMON reads it to decide whether there is anything to spawn at all. Each of the three
 * failure shapes — not in the catalog, the wrong kind, no block — is a SENTENCE here rather than a
 * null the caller has to interpret, so all three print the same thing.
 *
 * It resolves NOTHING transitively and fetches nothing: `deps` are already flattened by
 * `resolveAsset` and the data assets are the LIFETIME the handle owns. Pure.
 */
export function resolveEntityPrefab(catalog: LibraryCatalog, id: string): ResolvedEntityPrefab {
  const empty = { id, found: false, class: '', params: {}, data: [] as readonly string[] };
  const asset = catalog.assets[id];
  if (asset === undefined) {
    return { ...empty, warnings: [`entity prefab '${id}' is not in the library catalog`] };
  }
  if (asset.kind !== ENTITY_PREFAB_KIND) {
    return { ...empty, warnings: [`'${id}' is a ${asset.kind}, not an ${ENTITY_PREFAB_KIND} — only an entity asset names a class`] };
  }
  if (asset.entity === null) {
    // Refused by the catalog schema, so this is reachable only through a hand-built entry.
    return { ...empty, warnings: [`entity '${id}' carries no 'entity' block — there is no class to spawn`] };
  }
  return {
    id,
    found: true,
    class: asset.entity.class,
    params: asset.entity.params,
    data: asset.deps,
    warnings: [],
  };
}

// ── requires gate ──────────────────────────────────────────────────────────────────────────────

export interface RuntimeCapabilities {
  /** Registry class names this runtime can load (`Object.keys(ENTITY_REGISTRY)`). */
  classes: ReadonlySet<string>;
  /** Decoder formats this runtime has (`meshopt` today; `ktx2` when R2 lands). */
  formats: ReadonlySet<string>;
}

export interface RequiresVerdict {
  supported: boolean;
  missingClasses: string[];
  missingFormats: string[];
}

/**
 * The capability gate — `world.requires` (`schema.ts` `checkRequires`) generalised to the two
 * things an asset can need. An unsupported asset is a STATE (`failed('unsupported')`): the entry
 * stays in the catalog, the editor greys it, the store hides it (plan §2.2).
 */
export function checkAssetRequires(resolved: ResolvedAsset, caps: RuntimeCapabilities): RequiresVerdict {
  const missingClasses = resolved.requires.classes.filter((c) => !caps.classes.has(c));
  const missingFormats = resolved.requires.formats.filter((f) => !caps.formats.has(f));
  return { supported: missingClasses.length === 0 && missingFormats.length === 0, missingClasses, missingFormats };
}
